import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { defaultRange } from './format';

export interface DateRange {
  date_from: string;
  date_to: string;
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Zakres dat trzymany W ADRESIE, nie w stanie komponentu.
 *
 * Dzięki temu każdy widok da się wskazać linkiem: „zobacz tę szkołę w tym oknie” jest jednym
 * wklejeniem URL-a, a nie instrukcją, które filtry ustawić. Odświeżenie strony niczego nie gubi.
 */
export function useDateRange(): [DateRange, (next: Partial<DateRange>) => void] {
  const [params, setParams] = useSearchParams();
  const fallback = useMemo(() => defaultRange(), []);

  const range = useMemo<DateRange>(() => {
    const from = params.get('from');
    const to = params.get('to');
    const date_to = to && ISO.test(to) ? to : fallback.date_to;
    let date_from = from && ISO.test(from) ? from : fallback.date_from;
    if (date_from > date_to) date_from = date_to;
    return { date_from, date_to };
  }, [params, fallback]);

  const setRange = useCallback(
    (next: Partial<DateRange>) => {
      setParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          if (next.date_from) updated.set('from', next.date_from);
          if (next.date_to) updated.set('to', next.date_to);
          return updated;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return [range, setRange];
}

/** Gotowe okna do szybkiego wyboru. Zawsze liczone wstecz od dziś. */
export const RANGE_PRESETS: { label: string; days: number }[] = [
  { label: '7 dni', days: 7 },
  { label: '30 dni', days: 30 },
  { label: '90 dni', days: 90 },
];

export function presetRange(days: number, today = new Date()): DateRange {
  const iso = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Europe/Warsaw' });
  return {
    date_from: iso(new Date(today.getTime() - (days - 1) * 86400000)),
    date_to: iso(today),
  };
}
